/**
 * Директива превращает элемент в "кнопку" сортировки по атрибуту (attribute)
 *
 * Примеры:
 *      <td v-sort:attribute>           По нажатию на элемент, случится автопереход на новый URL.
 *      <td v-sort:attribute="{ name: 'column', order: 'asc' }">   По нажатию на элемент, value примет новое значение.
 *
 * Вариант работы через параметр используется для ручной локальной сортировки чего угодно.
 * Параметр меняется похожим образом с v-model.
 */
import Vue from 'vue'

const HANDLER_CLICK = '_vue_sort_click_handler'
const HANDLER_WATCH_ROUTE = '_vue_sort_watch_route_handler'

const CLASS_SORT = 'sort'
const CLASS_ASC = 'sort-up'
const CLASS_DESC = 'sort-down'

const ASC = 'asc'
const DESC = 'desc'

/**
 * Парсит строку вида '-id' в объект { name: 'id', order: 'desc' }.
 */
function parse(value) {
    if (!value || value.length === 0) {
        return null
    }

    return value.charAt(0) === '-'
        ? { name: value.slice(1), order: DESC }
        : { name: value, order: ASC }
}

/**
 * Возвращает фильтр для применения новой сортировки.
 *
 * На вход принимает ТЕКУЩУЮ сортировку (clause) и название атрибута (name), по которому надо
 * применить НОВУЮ сортировку.
 *
 * Примеры:
 *      null, 'id'                              => { order: 'id', page: 1 }
 *      { name: 'id', order: 'asc' }, 'id'      => { order: '-id', page: 1 }
 *      { name: 'id', order: 'desc' }, 'id'     => { order: '', page: 1 }
 *      { name: 'count', order: 'desc' }, 'id'  => { order: 'id', page: 1 }
 */
function resolveFilter(clause, name) {
    return (!clause || clause.name !== name)
        ? { order: name, page: 1 }
        : { order: clause.order === ASC ? `-${name}` : '', page: 1 }
}

/**
 * Устанавливает стили для атрибута (name) на основе ТЕКУЩЕЙ сортировки (clause).
 */
function classes(el, clause, name) {
    el.classList.remove(CLASS_ASC, CLASS_DESC)

    if (!clause || clause.name !== name) {
        return
    }

    el.classList.add(clause.order === ASC ? CLASS_ASC : CLASS_DESC)
}

/**
 * Добавляет контейнер с иконкой для отображения текущего состояния сортировки.
 */
function appendChild(el) {
    el.classList.add(CLASS_SORT)
    el.appendChild(document.createElement('span'))
}

/**
 * Вызывается, когда директива отключается от элемента. Убираем за собой.
 */
function unbind(el) {
    if (el[HANDLER_CLICK]) {
        el.removeEventListener('click', el[HANDLER_CLICK])
        delete el[HANDLER_CLICK]
    }

    if (el[HANDLER_WATCH_ROUTE]) {
        el[HANDLER_WATCH_ROUTE]()
        delete el[HANDLER_WATCH_ROUTE]
    }
}

export default {
    inserted(el, binding, vnode) {
        unbind(el)
        el.classList.remove(CLASS_SORT, CLASS_ASC, CLASS_DESC)

        const MODE_ROUTE = binding.value === undefined
        const name = binding.arg

        if (!name) {
            console.warn(`Директива v-${binding.name} в аргументе ожидает название атрибута, для которого применяется сортировка.`)
            return
        }

        // Контейнер с иконкой
        appendChild(el)

        // Применяем стили для текущего состояния сортировки
        const clause = MODE_ROUTE ? parse(vnode.context.$route.query.order) : binding.value
        classes(el, clause, name)

        // Вешаем обработчик на клик по элементу
        el[HANDLER_CLICK] = _ => {
            const clause = MODE_ROUTE ? parse(vnode.context.$route.query.order) : vnode.context[binding.expression]
            const filter = resolveFilter(clause, name)

            MODE_ROUTE
                ? vnode.context.applyFilter(filter)
                : Vue.set(vnode.context, binding.expression, parse(filter.order))
        }

        el.addEventListener('click', el[HANDLER_CLICK])

        // Для режима роутера вешаем вотчер на изменение сортировки в адресной строке.
        if (MODE_ROUTE) {
            el[HANDLER_WATCH_ROUTE] = vnode.context.$watch('$route.query.order', value => {
                classes(el, parse(value), name)
            })
        }
    },

    update(el, binding) {
        if (binding.arg && binding.value !== binding.oldValue) {
            classes(el, binding.value, binding.arg)
        }
    },

    unbind
}
